import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface UploadResponse {
  success: boolean;
  count: number;
  urls: string[];
  error?: string;
}

export interface DeleteResponse {
  status: string;
  message: string;
  fileName: string;
  error?: string;
}

export interface HealthResponse {
  status: string;
  bucket: string;
  region: string;
  timestamp: string;
}

export interface PdfUploadResponse {
  success: boolean;
  uploadedCount: number;
  urls: string[];
  errors?: string[];
}

export interface PdfDeleteResponse {
  status: string;
  message: string;
  key?: string;
  error?: string;
}

/** Réponse commune aux endpoints d'upload d'image (720x720, maquette, annonce) */
export interface ImageUploadResponse {
  success: boolean;
  uploadedCount: number;
  urls: string[];
  errors?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AwscontrolService {
  private baseUrl = 'https://app.titv-store-api.com/api/files';

  constructor(private http: HttpClient) { }

  private getToken(): string {
    try {
      const authUser = sessionStorage.getItem('auth-user');
      if (authUser) {
        const userObj = JSON.parse(authUser);
        return userObj.token || '';
      }
      return '';
    } catch {
      return '';
    }
  }

  private getHeaders(): HttpHeaders {
    const token = this.getToken();
    if (token) {
      return new HttpHeaders({
        'Authorization': 'Bearer ' + token
      });
    }
    return new HttpHeaders();
  }

  private handleError = (error: HttpErrorResponse) => {
    let errorMessage = 'Une erreur est survenue';
    if (error.status === 403) {
      errorMessage = `Accès interdit. Token: ${this.getToken() ? 'présent' : 'absent'}`;
    } else if (error.status === 401) {
      errorMessage = 'Non autorisé. Veuillez vous reconnecter.';
    } else {
      errorMessage = `Erreur ${error.status}: ${error.message}`;
    }
    console.error('Erreur AwscontrolService:', errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  /**
   * Génère un nom de fichier aléatoire unique
   * @param originalName - Nom original du fichier (pour conserver l'extension)
   * @returns Nom aléatoire (timestamp + aléatoire + extension)
   */
  private generateRandomFileName(originalName: string): string {
    const timestamp = Date.now();
    const randomPart = Math.random().toString(36).substring(2, 15);
    const extension = originalName.substring(originalName.lastIndexOf('.'));
    return `${timestamp}_${randomPart}${extension}`;
  }

  /**
   * Renomme un fichier avec un nom aléatoire en conservant son contenu et son type
   * @param file - Fichier d'origine
   * @returns Nouveau fichier avec un nom aléatoire
   */
  private renameFileWithRandomName(file: File): File {
    const newName = this.generateRandomFileName(file.name);
    return new File([file], newName, { type: file.type });
  }

  /**
   * Check AWS S3 connection health
   */
  checkHealth(): Observable<HealthResponse> {
    const headers = this.getHeaders();
    return this.http.get<HealthResponse>(`${this.baseUrl}/health`, { headers })
      .pipe(catchError(this.handleError));
  }

  /**
   * Upload single or multiple files to AWS S3 with random unique names
   * @param files - Array of files to upload
   * @param folder - Optional folder name in S3 bucket
   */
  uploadFiles(files: File[], folder?: string): Observable<UploadResponse> {
    // Renommer chaque fichier avec un nom aléatoire
    const renamedFiles = files.map(file => this.renameFileWithRandomName(file));

    const formData = new FormData();
    renamedFiles.forEach(file => formData.append('files', file, file.name));
    if (folder) {
      formData.append('folder', folder);
    }

    const headers = this.getHeaders();
    return this.http.post<UploadResponse>(`${this.baseUrl}/upload`, formData, { headers })
      .pipe(catchError(this.handleError));
  }

  /**
   * Upload a single file (convenience method) with random name
   * @param file - Single file to upload
   * @param folder - Optional folder name in S3 bucket
   */
  uploadFile(file: File, folder?: string): Observable<UploadResponse> {
    return this.uploadFiles([file], folder);
  }

  /**
   * Delete a file from AWS S3
   * @param fileUrl - Full URL of the file to delete
   */
  deleteFile(fileUrl: string): Observable<DeleteResponse> {
    const headers = this.getHeaders();
    const params = new HttpParams().set('fileUrl', fileUrl);
    return this.http.delete<DeleteResponse>(`${this.baseUrl}/delete`, { headers, params })
      .pipe(catchError(this.handleError));
  }

  // ==================== PDF‑SPECIFIC ENDPOINTS ====================

  /**
   * Upload PDF files (only .pdf allowed) to the 'magazinepdf' folder
   * Les PDF sont également renommés aléatoirement
   * @param files - Array of PDF files
   */
  uploadPdf(files: File[]): Observable<PdfUploadResponse> {
    const renamedFiles = files.map(file => this.renameFileWithRandomName(file));
    const formData = new FormData();
    renamedFiles.forEach(file => formData.append('files', file, file.name));

    const headers = this.getHeaders();
    return this.http.post<PdfUploadResponse>(`${this.baseUrl}/upload/pdf`, formData, { headers })
      .pipe(catchError(this.handleError));
  }

  /**
   * Delete a PDF file from the 'magazinepdf' folder
   * @param fileUrl - Full URL of the PDF file to delete
   */
  deletePdf(fileUrl: string): Observable<PdfDeleteResponse> {
    const headers = this.getHeaders();
    const params = new HttpParams().set('fileUrl', fileUrl);
    return this.http.delete<PdfDeleteResponse>(`${this.baseUrl}/delete/pdf`, { headers, params })
      .pipe(catchError(this.handleError));
  }

  // ==================== IMAGE‑SPECIFIC ENDPOINTS ====================

  /**
   * Upload image standard 720 × 720 (carré, fond flouté)
   * Folder par défaut : "imagesousproduit"
   * @param files  - Fichiers images à uploader
   * @param folder - Dossier S3 (optionnel, sinon "imagesousproduit")
   */
  imagesousproduit(files: File[], folder: string = 'imagesousproduit'): Observable<ImageUploadResponse> {
    const renamedFiles = files.map(file => this.renameFileWithRandomName(file));
    const formData = new FormData();
    renamedFiles.forEach(file => formData.append('files', file, file.name));
    if (folder) {
      formData.append('folder', folder);
    }

    const headers = this.getHeaders();
    return this.http.post<ImageUploadResponse>(`${this.baseUrl}/upload/image`, formData, { headers })
      .pipe(catchError(this.handleError));
  }

  /**
   * Upload image maquette produit 555 × 428 (72 DPI, fond flouté)
   * Folder par défaut : "imageproduit"
   * @param files  - Fichiers images à uploader
   * @param folder - Dossier S3 (optionnel, sinon "imageproduit")
   */
  imageproduit(files: File[], folder: string = 'imageproduit'): Observable<ImageUploadResponse> {
    const renamedFiles = files.map(file => this.renameFileWithRandomName(file));
    const formData = new FormData();
    renamedFiles.forEach(file => formData.append('files', file, file.name));
    if (folder) {
      formData.append('folder', folder);
    }

    const headers = this.getHeaders();
    return this.http.post<ImageUploadResponse>(`${this.baseUrl}/upload/imagemaqproduit`, formData, { headers })
      .pipe(catchError(this.handleError));
  }

  /**
   * Upload image principale annonce 624 × 365 (72 DPI, réduction bruit 40%, fond flouté)
   * Folder par défaut : "imageprincipaleannonce"
   * @param files  - Fichiers images à uploader
   * @param folder - Dossier S3 (optionnel, sinon "imageprincipaleannonce")
   */
  imageprincipaleannonce(files: File[], folder: string = 'imageprincipaleannonce'): Observable<ImageUploadResponse> {
    const renamedFiles = files.map(file => this.renameFileWithRandomName(file));
    const formData = new FormData();
    renamedFiles.forEach(file => formData.append('files', file, file.name));
    if (folder) {
      formData.append('folder', folder);
    }

    const headers = this.getHeaders();
    return this.http.post<ImageUploadResponse>(`${this.baseUrl}/upload/imageprincipaleannonce`, formData, { headers })
      .pipe(catchError(this.handleError));
  }

  // ==================== PROXY DOWNLOAD ENDPOINT ====================

  /**
   * Download a file via the backend proxy (bypasses CORS)
   * @param fileUrl - Full URL of the file to download (must be from our S3 bucket)
   * @returns Observable<Blob> - The file as a blob
   */
  downloadFile(fileUrl: string): Observable<Blob> {
    const headers = this.getHeaders();
    const params = new HttpParams().set('url', fileUrl);
    return this.http.get(`${this.baseUrl}/download`, {
      headers,
      params,
      responseType: 'blob'
    }).pipe(catchError(this.handleError));
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Extract filename from S3 URL
   */
  extractFileName(fileUrl: string): string {
    return fileUrl.substring(fileUrl.lastIndexOf('/') + 1);
  }

  /**
   * Generate S3 URL from filename (useful for displaying)
   */
  generateS3Url(fileName: string, folder?: string): string {
    const bucketName = 'tunisie-immob';
    const region = 'eu-west-3';
    if (folder) {
      return `https://${bucketName}.s3.${region}.amazonaws.com/${folder}/${fileName}`;
    }
    return `https://${bucketName}.s3.${region}.amazonaws.com/${fileName}`;
  }
}